import { validateStringData, NA } from 'utils/formatting';
import { sorterByDateCommon } from 'utils/sorting';

/**
 * Interfaces for clarity and type safety
 */
interface Security {
    id: string | number;
    currency?: {
        threeLetterIsoCode: string;
    };
    [key: string]: any;
}

interface AdaptTransactionsOptions {
    securities?: Security[];
    getFormattedDate?: (date: any) => string;
    getFormattedNumber?: (value: any, options?: Intl.NumberFormatOptions) => string;
    getFormattedCurrency?: (value: any, options?: { currency?: string } & Intl.NumberFormatOptions) => string;
}

interface TransactionItem {
    Id: string | number;
    Date: string;
    ExecutionDate?: string;
    SecurityId?: string | number;
    Type?: { Id: number };
    Price?: any;
    Quantity?: number;
    Value: number;
    BookingType?: string;
    BookingText?: string;
    CustomAttributes?: {
        TrxCodeText?: string;
    };
}

export const adaptTransactions = (data: any, options: AdaptTransactionsOptions = {}) => {
    const { securities = [] } = options;
    const getFormattedDate = options?.getFormattedDate || ((d: any) => d);
    const getFormattedNumber = options?.getFormattedNumber || ((d: any) => d);
    const getFormattedCurrency = options?.getFormattedCurrency || ((d: any) => d);

    const transactions: TransactionItem[] = data?.Transactions || [];
    const currency = data?.Currency?.CurrencyCode;

    // Create a lookup map for securities to avoid O(n^2) lookups inside the map
    const securitiesCurrency = securities.reduce<Record<string, Security>>((acc, item) => {
        acc[item.id] = item;

        return acc;
    }, {});

    let balance = 0;

    return transactions
        .sort((...rest) => sorterByDateCommon('Date')(...rest))
        .map((item) => {
            // Determine if the transaction is an inflow or outflow
            const multiplier = item?.Type?.Id === 1 ? 1 : -1;
            const validatePrice = validateStringData(item?.Price);

            // Safe access to nested security currency data
            const securityId = item?.SecurityId;
            const priceCurrencyCode = securityId ? securitiesCurrency[securityId]?.currency?.threeLetterIsoCode : undefined;

            const formattedNumber = item?.Quantity
                ? getFormattedNumber(item.Quantity, {
                    maximumFractionDigits: 6,
                    minimumFractionDigits: 6,
                })
                : NA;

            // Update running balance
            balance += multiplier * (item?.Value || 0);

            return {
                Name: validateStringData(item?.CustomAttributes?.TrxCodeText),
                Number: formattedNumber,
                Date: getFormattedDate(item?.Date),
                Balance: getFormattedNumber(balance, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                }),
                ExecutionDate: getFormattedDate(item?.ExecutionDate),
                BookingType: item?.BookingType,
                Price: getFormattedCurrency(validatePrice, { currency: priceCurrencyCode }),
                Text: item?.BookingText,
                Value: getFormattedNumber(multiplier * (item?.Value || 0), {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                }),
                Total: getFormattedCurrency(item?.Value, {
                    currency,
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                }),
            };
        });
};
